import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Gift, Loader2, Copy, Check, PartyPopper } from "lucide-react";
import { DISCOUNT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "ps_discount_seen";
const CODE_KEY = "ps_discount_code";

export default function DiscountPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(localStorage.getItem(CODE_KEY) || null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    let armed = false;
    const arm = () => { armed = true; };
    // arm after cookie decision, or after 9s fallback
    window.addEventListener("ps-cookie-decided", arm);
    const cookieDecided = !!localStorage.getItem("ps_cookie_consent");
    if (cookieDecided) armed = true;
    const t = setTimeout(() => {
      if (armed || cookieDecided) setOpen(true);
    }, cookieDecided ? 6000 : 9000);
    return () => { clearTimeout(t); window.removeEventListener("ps-cookie-decided", arm); };
  }, []);

  const close = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Inserisci la tua email."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/coupon`, { email });
      localStorage.setItem(SEEN_KEY, "1");
      if (data.code) {
        setCode(data.code);
        localStorage.setItem(CODE_KEY, data.code);
      } else {
        toast.success("Iscrizione completata! Riceverai il tuo codice sconto via email.");
        setOpen(false);
      }
    } catch {
      toast.error("Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#1f2937]/60 backdrop-blur-sm" onClick={close} />
      <div data-testid={DISCOUNT.modal} className="relative w-full max-w-md bg-white rounded-[1.75rem] shadow-2xl overflow-hidden">
        <button
          data-testid={DISCOUNT.close}
          onClick={close}
          aria-label="Chiudi"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-[#4b5563] shadow"
        >
          <X size={18} />
        </button>

        <div className="bg-[#5982c5] px-8 py-7 text-center text-white">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#f0d203] flex items-center justify-center">
            <Gift size={28} className="text-[#1a1a1a]" />
          </div>
          <p className="mt-4 font-heading text-2xl font-extrabold">5% di sconto extra</p>
          <p className="mt-1 text-white/85 text-sm">Iscriviti ora e ricevi subito il tuo codice personale.</p>
        </div>

        <div className="p-8">
          {!code ? (
            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid={DISCOUNT.email}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email"
                className="w-full px-4 py-3 rounded-xl border border-[#d1d5db] text-[#1f2937] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#5982c5] focus:border-transparent"
              />
              <button
                data-testid={DISCOUNT.submit}
                type="submit"
                disabled={loading}
                className="w-full bg-[#f0d203] text-[#1a1a1a] hover:bg-[#dcb902] font-bold px-6 py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> Genero il codice…</> : "Ottieni il 5% di sconto"}
              </button>
              <p className="text-center text-xs text-[#9ca3af]">Niente spam. Un solo utilizzo per utente.</p>
            </form>
          ) : (
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-[#ecfef7] flex items-center justify-center">
                <PartyPopper size={24} className="text-[#5982c5]" />
              </div>
              <p className="mt-3 font-heading font-bold text-[#1f2937]">Ecco il tuo codice!</p>
              <p className="text-sm text-[#4b5563]">Inseriscilo al checkout per il 5% di sconto.</p>
              <div className="mt-4 flex items-center gap-2">
                <code data-testid={DISCOUNT.code} className="flex-1 font-heading font-extrabold text-lg tracking-wider text-[#5982c5] bg-[#ecfef7] border-2 border-dashed border-[#5982c5]/40 rounded-xl py-3">
                  {code}
                </code>
                <button
                  data-testid={DISCOUNT.copy}
                  onClick={copy}
                  className="shrink-0 w-12 h-12 rounded-xl bg-[#5982c5] text-white flex items-center justify-center hover:bg-[#4a6fa8]"
                  aria-label="Copia codice"
                >
                  {copied ? <Check size={20} /> : <Copy size={18} />}
                </button>
              </div>
              <button onClick={close} className="mt-5 text-sm font-semibold text-[#5982c5] hover:underline">
                Continua lo shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
